import { forwardRef, useImperativeHandle, useCallback } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { CaptchaRef, ReCaptchaProps } from '../../types/captcha.type';

const ReCaptchaInner = forwardRef<CaptchaRef, Omit<ReCaptchaProps, 'siteKey'>>(
  ({ onVerify, onError }, ref) => {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const run = useCallback(async () => {
      if (!executeRecaptcha) return undefined;
      try {
        const token = await executeRecaptcha('submit');
        if (token && onVerify) onVerify(token);
        return token;
      } catch (err) {
        console.error('reCAPTCHA execution error:', err);
        onError?.();
        return undefined;
      }
    }, [executeRecaptcha, onVerify, onError]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        void run();
      },
      execute: () => run(),
    }), [run]);

    // Invisible v3: do not execute on mount. Running grecaptcha on load
    // focuses a hidden iframe; blurring the window then remounts the
    // script and api.js can document.write / reload the page.
    return null;
  }
);
ReCaptchaInner.displayName = 'ReCaptchaInner';

export const ReCaptcha = forwardRef<CaptchaRef, ReCaptchaProps>(
  ({ siteKey, ...props }, ref) => {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={siteKey}
        useEnterprise={false}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
        }}
      >
        <ReCaptchaInner {...props} ref={ref} />
      </GoogleReCaptchaProvider>
    );
  }
);
ReCaptcha.displayName = 'ReCaptcha';
